import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Clock, Users, CalendarDays, Timer, ArrowRight, Pin } from "lucide-react";
import { useApi } from "@/lib/api";
import { useMember } from "@/context/MemberContext";
import { fmt, hoursNL, timeOf } from "@/lib/dates";
import { PageHeader, Panel, Loader, Avatar, Empty } from "@/components/common";
import { Button } from "@/components/ui/button";
import { OwnerChecklist } from "@/components/OwnerChecklist";

const Stat = ({ icon: Icon, label, value, sub, id, accent }) => (
  <Panel className="fade-up" data-testid={`stat-${id}`}>
    <div className="flex items-center justify-between">
      <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</p>
      <Icon className={`h-5 w-5 ${accent ? "text-[#FF5A36]" : "text-[#0D5C46]"}`} />
    </div>
    <p className="mt-3 font-display text-3xl font-bold text-slate-900 font-mono-num" data-testid={`stat-${id}-value`}>{value}</p>
    {sub && <p className="text-xs text-slate-500 mt-1">{sub}</p>}
  </Panel>
);

const greeting = () => {
  const h = new Date().getHours();
  return h < 12 ? "Goedemorgen" : h < 18 ? "Goedemiddag" : "Goedenavond";
};

export default function Dashboard() {
  const api = useApi();
  const { member, isManager } = useMember();
  const [d, setD] = useState(null);

  useEffect(() => {
    api.get("/dashboard").then((r) => setD(r.data));
  }, [api]);

  if (!d) return <Loader />;
  const first = member.name.split(" ")[0];

  return (
    <div data-testid="dashboard-page">
      <PageHeader title={`${greeting()}, ${first}`} subtitle={fmt(d.today, "EEEE d MMMM yyyy")}>
        <Button asChild className="rounded-full bg-[#FF5A36] hover:bg-[#E04826]" data-testid="dashboard-clock-button">
          <Link to="/klokken"><Clock className="h-4 w-4 mr-2" />Naar klokken</Link>
        </Button>
        {isManager && (
          <Button asChild variant="outline" className="rounded-full" data-testid="dashboard-plan-button">
            <Link to="/planning"><CalendarDays className="h-4 w-4 mr-2" />Dienst plannen</Link>
          </Button>
        )}
      </PageHeader>

      {d.setup && <OwnerChecklist setup={d.setup} />}

      {d.swaps_pending > 0 && (
        <Link to="/ruilen" className="mb-6 flex items-center justify-between gap-3 rounded-2xl bg-sky-50 border border-sky-200 px-4 py-3 text-sky-900 hover:bg-sky-100 transition-colors fade-up" data-testid="dashboard-swaps-banner">
          <span className="text-sm font-semibold">
            {isManager ? `${d.swaps_pending} ${d.swaps_pending === 1 ? "ruilverzoek wacht" : "ruilverzoeken wachten"} op je goedkeuring` : `${d.swaps_pending} ${d.swaps_pending === 1 ? "dienst kun" : "diensten kun"} je overnemen`}
          </span>
          <ArrowRight className="h-4 w-4" />
        </Link>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <Stat id="clocked-in" icon={Timer} label="Nu ingeklokt" value={d.clocked_in.length} sub={`van ${d.employee_count} medewerkers`} accent />
        <Stat id="today-shifts" icon={CalendarDays} label="Vandaag ingepland" value={d.today_shifts.length} sub="diensten" />
        <Stat id="my-hours" icon={Clock} label="Mijn uren" value={hoursNL(d.my_week_hours)} sub={`deze week · ${hoursNL(d.my_week_planned)} gepland`} />
        {isManager ? (
          <Stat id="team-hours" icon={Users} label="Teamuren" value={hoursNL(d.team_week_hours)} sub="deze week" />
        ) : (
          <Stat id="next-shift" icon={CalendarDays} label="Volgende dienst" value={d.next_shift ? fmt(d.next_shift.date, "EEE d") : "—"} sub={d.next_shift ? `${d.next_shift.start} – ${d.next_shift.end}` : "nog niets gepland"} />
        )}
      </div>

      <div className="grid lg:grid-cols-[1.4fr_1fr] gap-4 mt-6">
        <Panel className="fade-up fade-up-2" data-testid="today-shifts-panel">
          <h2 className="font-semibold text-lg mb-4">Rooster van vandaag</h2>
          {d.today_shifts.length === 0 ? (
            <Empty icon={CalendarDays} title="Geen diensten vandaag" text={isManager ? "Plan diensten in via Planning." : "Bekijk de weekplanning voor je komende diensten."} />
          ) : (
            <ul className="divide-y divide-slate-100">
              {d.today_shifts.map((s) => (
                <li key={s.id} className="flex items-center gap-3 py-3" data-testid={`today-shift-${s.id}`}>
                  <Avatar name={s.name} />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{s.name}</p>
                    <p className="text-xs text-slate-500">{s.position || "Dienst"}</p>
                  </div>
                  <span className="font-mono-num text-sm font-semibold text-slate-700">{s.start} – {s.end}</span>
                </li>
              ))}
            </ul>
          )}
        </Panel>

        <div className="space-y-4">
          <Panel className="fade-up fade-up-3" data-testid="clocked-in-panel">
            <h2 className="font-semibold text-lg mb-3">Wie is er nu?</h2>
            {d.clocked_in.length === 0 ? (
              <p className="text-sm text-slate-500">Niemand is op dit moment ingeklokt.</p>
            ) : (
              <ul className="space-y-2">
                {d.clocked_in.map((c) => (
                  <li key={c.user_id} className="flex items-center gap-3">
                    <span className="h-2 w-2 rounded-full bg-emerald-500" />
                    <span className="flex-1 text-sm font-medium">{c.name}</span>
                    <span className="text-xs text-slate-500">sinds {timeOf(c.clock_in)}</span>
                  </li>
                ))}
              </ul>
            )}
          </Panel>
          <Panel className="fade-up fade-up-4" data-testid="recent-messages-panel">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-semibold text-lg">Prikbord</h2>
              <Link to="/prikbord" className="text-sm font-medium text-[#0D5C46] inline-flex items-center" data-testid="dashboard-board-link">
                Alles <ArrowRight className="h-3.5 w-3.5 ml-1" />
              </Link>
            </div>
            {d.recent_messages.length === 0 ? (
              <p className="text-sm text-slate-500">Nog geen berichten.</p>
            ) : (
              <ul className="space-y-3">
                {d.recent_messages.map((msg) => (
                  <li key={msg.id} className="text-sm">
                    <p className="text-slate-800 line-clamp-2">{msg.pinned && <Pin className="inline h-3.5 w-3.5 mr-1 text-[#FF5A36]" />}{msg.text}</p>
                    <p className="text-xs text-slate-500 mt-0.5">{msg.author_name} · {fmt(msg.created_at, "d MMM HH:mm")}</p>
                  </li>
                ))}
              </ul>
            )}
          </Panel>
        </div>
      </div>
    </div>
  );
}
